import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { proposalApi, Proposal, ProposalStatus, AcceptProposalRequest } from '../api/proposalApi';
import { BrandingData, getBrandingByUserId } from '../api/brandingApi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import Spinner from '../components/Spinner';
import SignaturePad from '../components/SignaturePad';
import { toast } from 'react-toastify';
import { CheckCircle2, User, MapPin, Building, ShieldCheck, XCircle } from 'lucide-react';

const cleanMarkdown = (text: string) => {
  if (!text) return '';
  return text
    .replace(/__##/g, '##')
    .replace(/##(\d)/g, '## $1')
    .replace(/([^\n])\n(#{1,2}\s+)/g, '$1\n\n$2')
    .replace(/\n---/g, '\n\n---\n\n');
};

export default function PublicProposalPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [branding, setBranding] = useState<BrandingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [isDeclined, setIsDeclined] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '', address: '', address2: '', city: '', state: '', zip: '', companyName: '', marketingConsent: 'no', agreedToTerms: false, signature: ''
  });

  const fetchData = useCallback(async () => {
  try {
    const p = await proposalApi.getPublicProposal(token!);

    const b = p.userId
      ? await getBrandingByUserId(p.userId)
      : null;

    setProposal(p);
    setBranding(b);

    if (p.status === ProposalStatus.Accepted) {
      setIsAccepted(true);
    }

    if (p.status === ProposalStatus.Declined) {
      setIsDeclined(true);
    }
  } catch (err) {
    toast.error("Proposal invalid or expired");
  } finally {
    setLoading(false);
  }
}, [token]);

  useEffect(() => {
    const shouldShowForm = searchParams.get('showForm') === 'true';

    if (
      shouldShowForm 
      // && proposal?.status === ProposalStatus.Sent
    ) {
      setShowForm(true);
    }
  }, [searchParams, proposal]);

  useEffect(() => { if (token) fetchData(); }, [token, fetchData]);

  const handleDecline = async () => {
    if (!token) return;
    setIsDeclining(true);
    try {
      await proposalApi.declineProposal(token);
      setIsDeclined(true);
      toast.info("Proposal declined");
    } catch (err) {
      // Handled by API wrapper
    } finally {
      setIsDeclining(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.agreedToTerms) return toast.warning("Please agree to the Terms of Service");
    if (!formData.signature) return toast.warning("Signature is required");

    setIsSubmitting(true);
    try {
      const payload: AcceptProposalRequest = {
        ...formData,
        address: `${formData.address}, ${formData.address2 ? formData.address2 + ', ' : ''}${formData.city}, ${formData.state} ${formData.zip}`,
      };
      await proposalApi.acceptProposal(token!, payload);
      setIsAccepted(true);
      toast.success("Proposal Accepted!");
    } catch (err) { toast.error("Submission failed"); } finally { setIsSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Spinner /></div>;
  if (!proposal) return <div className="min-h-screen flex items-center justify-center font-bold text-slate-500 uppercase tracking-widest">Link Invalid</div>;

  const logoUrl = branding?.logoUrl;
  const companyName = branding?.companyName || 'Strivre Services';

  const getHeaderMarkdown = () => {
    const dateStr = new Date(proposal.expiryDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const titlePart = proposal.title || '[Proposal Title]';
    // const namePart = proposal.customerName ? ` for ${proposal.customerName}` : '';
    const fromName = companyName;
    return `# ${titlePart}\n\n**Type:** ${proposal.documentType || 'Business Proposal'}  \n**Date:** ${dateStr}  \n**Prepared For:** ${proposal.customerName || '[Client Name]'}  \n**From:** ${fromName}\n\n---`;
  };

  const getFullMarkdown = () => {
    return `${getHeaderMarkdown()}\n\n${proposal.content}`;
  };

  if (isAccepted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white p-12 rounded-[3rem] shadow-2xl border border-slate-100 text-center animate-scale-in">
          <div className="w-20 h-20 bg-strivre-lightburgundy text-strivre-burgundy rounded-full flex items-center justify-center mx-auto mb-6"><CheckCircle2 size={48} /></div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Proposal Accepted Successfully</h1>
          <p className="text-slate-500 font-medium">Thank you for your business. We will be in touch shortly.</p>
        </div>
      </div>
    );
  }

  if (isDeclined) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white p-12 rounded-[3rem] shadow-2xl border border-slate-100 text-center animate-scale-in">
          <div className="w-20 h-20 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6"><XCircle size={48} /></div>
          <h1 className="text-2xl font-black text-slate-900 mb-2">Proposal Declined</h1>
          <p className="text-slate-500 font-medium">We appreciate your consideration. If you change your mind, please reach out to us.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-strivre-softgrey py-12 px-4">
      <div className="max-w-4xl mx-auto space-y-12">
        <div className="flex flex-col items-center text-center">
          {logoUrl ? <img src={logoUrl} alt={companyName} className="h-16 w-auto object-contain" /> : <div className="w-16 h-16 bg-strivre-burgundy rounded-2xl flex items-center justify-center font-bold text-white text-3xl">{companyName.charAt(0)}</div>}
          <h1 className="text-4xl font-bold text-slate-900 tracking-tight">{companyName}</h1>
          {/* <div> */}
            {/* <h1 className="text-slate-400 font-bold uppercase tracking-[0.2em] text-[10px] mb-1">{companyName}</h1> */}
            {/* <h1 className="text-4xl font-bold text-slate-900 tracking-tight">{companyName}</h1>
          </div> */}
        </div>

        {!showForm ? (
          <div className="bg-white rounded-[2.5rem] shadow-xl border border-strivre-lightnavy overflow-hidden animate-fade-in">
            <div 
              className="
                p-8 sm:p-16 prose max-w-none text-left bg-white
                prose-headings:!text-slate-900
                prose-h1:!text-slate-900
                prose-h2:!text-slate-900
                prose-p:text-slate-600
                prose-li:text-slate-600
                prose-strong:text-slate-900
                prose-headings:font-black
                prose-headings:tracking-tight
                prose-ul:list-disc prose-ul:pl-5
                prose-ol:list-decimal prose-ol:pl-5
              "
            >
              <ReactMarkdown 
                  remarkPlugins={[remarkGfm, remarkBreaks]}
                components={{
                  h1: ({ children, ...props }) => <h1 style={{ color: "#0f172a" }} {...props}>{children}</h1>,
                  h2: ({ children, ...props }) => <h2 style={{ color: "#0f172a" }} {...props}>{children}</h2>,
                  h3: ({ children, ...props }) => <h3 style={{ color: "#0f172a" }} {...props}>{children}</h3>,
                  h4: ({ children, ...props }) => <h4 style={{ color: "#0f172a" }} {...props}>{children}</h4>,
                  p: ({ ...props }) => <p style={{ color: "#334155" }} className="mb-4 leading-relaxed" {...props} />,
                  blockquote: ({ ...props }) => (
                    <blockquote 
                      style={{ 
                        borderLeftColor: "#7A1F2B", 
                        backgroundColor: "#f8fafc", 
                        color: "#475569" 
                      }} 
                      className="border-l-4 p-4 my-6 italic rounded-r-xl" 
                      {...props} 
                    />
                  ),
                  ul: ({ ...props }) => <ul style={{ color: "#334155" }} className="list-disc pl-5 my-2 space-y-1" {...props} />,
                  ol: ({ ...props }) => <ol style={{ color: "#334155" }} className="list-decimal pl-5 my-2 space-y-1" {...props} />,
                  li: ({ ...props }) => <li style={{ color: "#334155" }} className="pl-1" {...props} />,
                  hr: ({ ...props }) => <hr className="my-12 border-t-2 border-slate-100" style={{ borderColor: "#e2e8f0" }} {...props} />,
                  table: ({ ...props }) => <table className="w-full border-collapse border border-slate-300 my-6 text-sm" {...props} />,
                  thead: ({ ...props }) => <thead className="bg-strivre-softgrey" {...props} />,
                  th: ({ ...props }) => <th className="border border-slate-300 px-4 py-2 text-left font-bold" style={{ color: "#0f172a" }} {...props} />,
                  td: ({ ...props }) => <td className="border border-slate-300 px-4 py-2 text-slate-600" {...props} />
                }}
              >
                {cleanMarkdown(getFullMarkdown())}
              </ReactMarkdown>
            </div>
            {branding?.about && (
              <div className="p-8 sm:p-12 border-t border-slate-100 bg-strivre-softgrey">
                <h3 className="text-lg font-bold text-slate-900 mb-2 text-left">About Us</h3>
                <div className="prose max-w-none text-left">
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm, remarkBreaks]}
                    components={{
                      h1: ({ children, ...props }) => <h1 style={{ color: "#0f172a" }} {...props}>{children}</h1>,
                      h2: ({ children, ...props }) => <h2 style={{ color: "#0f172a" }} {...props}>{children}</h2>,
                      h3: ({ children, ...props }) => <h3 style={{ color: "#0f172a" }} {...props}>{children}</h3>,
                      h4: ({ children, ...props }) => <h4 style={{ color: "#0f172a" }} {...props}>{children}</h4>,
                      p: ({ ...props }) => <p style={{ color: "#334155" }} className="mb-4 leading-relaxed" {...props} />,
                      ul: ({ ...props }) => <ul style={{ color: "#334155" }} className="list-disc pl-5 my-2 space-y-1" {...props} />,
                      li: ({ ...props }) => <li style={{ color: "#334155" }} className="pl-1" {...props} />,
                    }}
                  >
                    {cleanMarkdown(branding.about)}
                  </ReactMarkdown>
                </div>
              </div>
            )}
            {proposal.status === ProposalStatus.Sent && (
              <div className="p-8 sm:p-12 border-t border-slate-100 bg-strivre-softgrey text-center space-y-4">
                <div className="max-w-lg mx-auto">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Next Step</h3>
                  <p className="text-slate-500 text-sm mb-6">
                    If you are happy with this proposal and would like to proceed, please click Approve below to fill in your details and accept.
                  </p>
                  <div className="flex gap-4">
                  <button 
                    onClick={() => setShowForm(true)} 
                    className="flex-1 bg-strivre-burgundy text-white px-8 py-4 rounded-2xl font-bold text-lg shadow-lg shadow-strivre-burgundy/20 hover:bg-red-900 transition-all active:scale-95"
                  >
                    Approve
                  </button>
                  <button 
                    onClick={handleDecline}
                    disabled={isDeclining || proposal.status !== ProposalStatus.Sent}
                    className="flex-1 border border-red-300 text-red-600 px-8 py-4 rounded-2xl font-bold text-lg hover:bg-red-50 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isDeclining ? <Spinner size="h-5 w-5" /> : 'Decline'}
                  </button>
                </div>
              </div>
            </div>
            )}
          </div>
        ) : (
          <div className="max-w-3xl mx-auto bg-white rounded-[3rem] shadow-2xl border border-slate-100 overflow-hidden animate-fade-in">
            <div className="p-10 bg-strivre-burgundy text-white">
              <h2 className="text-3xl font-bold tracking-tight mb-2 text-white">Service Confirmation</h2>
              <p className="text-white/80 font-medium">Review and complete the form below to begin.</p>
            </div>

            <form onSubmit={handleSubmit} className="p-8 sm:p-12 space-y-10 text-left">
              <div className="space-y-6">
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2 border-b border-slate-100 pb-4"><User size={20} className="text-strivre-burgundy" /> Customer Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2"><label className="text-sm font-semibold text-slate-700">First Name</label><input required className="input-base" type="text" value={formData.firstName} onChange={e => setFormData({...formData, firstName: e.target.value})} /></div>
                  <div className="space-y-2"><label className="text-sm font-semibold text-slate-700">Last Name</label><input required className="input-base" type="text" value={formData.lastName} onChange={e => setFormData({...formData, lastName: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2"><label className="text-sm font-semibold text-slate-700">Email</label><input required className="input-base" type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} /></div>
                  <div className="space-y-2"><label className="text-sm font-semibold text-slate-700">Phone</label><input required className="input-base" type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} /></div>
                </div>
              </div>

              <div className="space-y-6">
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2 border-b border-slate-100 pb-4"><MapPin size={20} className="text-strivre-burgundy" /> Mailing Address</h3>
                <div className="space-y-4">
                  <div className="space-y-2"><label className="text-sm font-semibold text-slate-700">Street</label><input required className="input-base" type="text" value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} /></div>
                  <div className="space-y-2"><label className="text-sm font-semibold text-slate-700">Line 2</label><input className="input-base" type="text" value={formData.address2} onChange={e => setFormData({...formData, address2: e.target.value})} /></div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                    <div className="col-span-2 md:col-span-1 space-y-2"><label className="text-sm font-semibold text-slate-700">City</label><input required className="input-base" type="text" value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} /></div>
                    <div className="space-y-2"><label className="text-sm font-semibold text-slate-700">State</label><input required className="input-base" type="text" value={formData.state} onChange={e => setFormData({...formData, state: e.target.value})} /></div>
                    <div className="space-y-2"><label className="text-sm font-semibold text-slate-700">ZIP</label><input required className="input-base" type="text" value={formData.zip} onChange={e => setFormData({...formData, zip: e.target.value})} /></div>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2 border-b border-slate-100 pb-4"><Building size={20} className="text-strivre-burgundy" /> Company</h3>
                <div className="space-y-2"><label className="text-sm font-semibold text-slate-700">Company Name</label><input required className="input-base" type="text" value={formData.companyName} onChange={e => setFormData({...formData, companyName: e.target.value})} /></div>
              </div>

              <div className="space-y-8 p-8 bg-slate-50 rounded-[2rem] border border-slate-100">
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2"><ShieldCheck size={20} className="text-strivre-burgundy" /> Terms of Service</h3>
                  <p className="text-sm text-slate-500 font-medium">By submitting, you agree to the {branding?.termsOfService && <a href={branding.termsOfService} target="_blank" rel="noopener noreferrer" className="text-strivre-burgundy underline">Terms of Service.</a>}</p>
                  <div className="flex items-center gap-3">
                    <input id="tos" type="checkbox" className="w-5 h-5 rounded border-slate-300 text-strivre-burgundy cursor-pointer" checked={formData.agreedToTerms} onChange={e => setFormData({...formData, agreedToTerms: e.target.checked})} />
                    <label htmlFor="tos" className="text-sm font-bold text-slate-700 cursor-pointer">I agree to the Terms of Service</label>
                  </div>
                </div>
                <div className="space-y-2"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">E-Signature</label><SignaturePad onSave={(sig) => setFormData({...formData, signature: sig})} onClear={() => setFormData({...formData, signature: ''})} /></div>
              </div>

              <button 
                disabled={isSubmitting || proposal.status !== ProposalStatus.Sent}
                className="btn-primary w-full py-4 rounded-2xl flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? <Spinner size="h-6 w-6" /> : "Confirm & Accept Proposal"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
